export type Coordinate2D = [number, number];

// =============================================================================
// BOUNDING BOX
// =============================================================================

/**
 * Bounding box configuration with named properties.
 * All values are in user coordinates.
 */
export interface BoundingBoxConfig {
  /** Left edge of the visible area (minimum x) */
  xStart: number;
  /** Right edge of the visible area (maximum x) */
  xEnd: number;
  /** Bottom edge of the visible area (minimum y) */
  yStart: number;
  /** Top edge of the visible area (maximum y) */
  yEnd: number;
}

/** @internal JSXGraph native bounding box format: [left, top, right, bottom] */
export type BoundingBoxTuple = [number, number, number, number];

/**
 * Convert BoundingBoxConfig to JSXGraph format [left, top, right, bottom].
 * @internal Used internally to communicate with JSXGraph API.
 */
export function boundingBoxToTuple(bbox: BoundingBoxConfig): BoundingBoxTuple {
  return [bbox.xStart, bbox.yEnd, bbox.xEnd, bbox.yStart];
}

// =============================================================================
// GRID
// =============================================================================

export type GridType = 'lines' | 'dots';

export interface GridConfig {
  /** Grid type: 'lines' extends from axis ticks, 'dots' renders points at intersections */
  type?: GridType;
  /** Whether the grid is visible. @default false */
  visible?: boolean;
}

// =============================================================================
// TICKS
// =============================================================================

/**
 * Tick label format presets.
 * - 'pi': Labels as multiples of π (0, π/2, π, 3π/2, 2π, ...)
 * - 'degrees': Labels as degrees (0°, 90°, 180°, ...)
 * - 'default': Standard numeric labels
 */
export type TickLabelFormat = 'pi' | 'degrees' | 'default';

export interface TickLabelConfig {
  /** Font size in pixels */
  fontSize?: number;
  /** Label offset from tick [x, y] in pixels */
  offset?: [number, number];
}

export interface TicksConfig {
  /** Whether ticks are visible at all. When false, hides both tick marks and labels. @default true */
  visible?: boolean;
  /** Distance between major ticks in user coordinates */
  ticksDistance?: number;
  /** Preset label format for common use cases (pi, degrees, default) */
  labelFormat?: TickLabelFormat;
  /** Number of minor ticks between two major ticks */
  minorTicks?: number;
  /** Height of major ticks in pixels. Use -1 to extend to grid lines */
  majorHeight?: number;
  /** Height of minor ticks in pixels */
  minorHeight?: number;
  /** Show labels on ticks */
  drawLabels?: boolean;
  /** Show label at origin (zero) */
  drawZero?: boolean;
  /** Scale factor for tick label values */
  scale?: number;
  /** Symbol to append to labels (e.g., "π") */
  scaleSymbol?: string;
  /** Custom array of tick positions (overrides ticksDistance) */
  fixedTicks?: number[];
  /** Custom array of tick labels (must match fixedTicks length) */
  labels?: string[];
  /** Label styling options */
  label?: TickLabelConfig;
}

// =============================================================================
// AXIS LABEL
// =============================================================================

export interface AxisLabelConfig {
  /** Label text (e.g., "x", "y", "θ") */
  text: string;
  /** Offset from axis endpoint [x, y] in pixels */
  offset?: [number, number];
  /** Font size in pixels */
  fontSize?: number;
}

// =============================================================================
// AXIS
// =============================================================================

/** Axis orientation */
export type AxisOrientation = 'horizontal' | 'vertical';

export interface AxisConfig {
  /** Unique identifier for the axis */
  id: string;
  /** Axis orientation: 'horizontal' (x-like) or 'vertical' (y-like) */
  orientation: AxisOrientation;
  /**
   * Position of the axis in user coordinates.
   * - For horizontal axes: y-coordinate where the axis is placed
   * - For vertical axes: x-coordinate where the axis is placed
   * - Use 0 for axes through the origin
   * @default 0
   */
  position?: number;
  /**
   * Whether this is a primary axis.
   * Primary axes control grid line generation when grid type is 'lines'.
   * Typically, you have one primary horizontal and one primary vertical axis.
   * @default true for the first axis of each orientation, false otherwise
   */
  isPrimary?: boolean;
  /** Tick configuration for this axis */
  ticks?: TicksConfig;
  /** Axis label configuration */
  axisLabel?: AxisLabelConfig;
}

// =============================================================================
// AXES CONFIG
// =============================================================================

export interface AxesConfig {
  /** Whether axes are visible. @default true */
  visible?: boolean;
  /** Custom axis configurations. Omit for default x and y axes through origin. */
  custom?: AxisConfig[];
}

// =============================================================================
// COORDINATE SYSTEM
// =============================================================================

export interface CoordinateSystemConfig {
  /**
   * Bounding box defining the visible area.
   * Specify as: { xStart, xEnd, yStart, yEnd }
   */
  boundingbox?: BoundingBoxConfig;
  /**
   * Lock the aspect ratio to enforce equal units on both axes (unitX === unitY).
   * Specifies which axis's bounding box range is preserved; the other axis adjusts.
   * - 'x': X range is preserved exactly, Y range adjusts to fill container
   * - 'y': Y range is preserved exactly, X range adjusts to fill container
   * When omitted, no aspect ratio constraint is applied.
   */
  fixedAxis?: 'x' | 'y';
  /**
   * Grid configuration.
   * Omit for default grid (lines type, hidden).
   * Use `{ visible: true }` to show. Use `{ type: 'dots' }` to customize.
   *
   * Note: For 'lines' grid, spacing is derived from primary axis tick distances.
   * For 'dots' grid, dots are placed at intersections of primary axis ticks.
   */
  grid?: GridConfig;
  /**
   * Axes configuration.
   * Omit for default axes (x and y through origin, visible).
   * Use `{ visible: false }` to hide all axes.
   * Use `{ custom: [...] }` for custom axis definitions.
   *
   * @example Two x-axes (radians and degrees)
   * ```ts
   * axes: { custom: [
   *   { id: 'x-rad', orientation: 'horizontal', position: 0, ticks: { labelFormat: 'pi' } },
   *   { id: 'x-deg', orientation: 'horizontal', position: -2, ticks: { labelFormat: 'degrees' } },
   *   { id: 'y', orientation: 'vertical', position: 0 },
   * ] }
   * ```
   */
  axes?: AxesConfig;
  /**
   * Global ticks configuration applied to all axes.
   * Individual axis ticks config takes precedence over this.
   *
   * @example Hide tick labels on all axes
   * ```ts
   * ticks: { drawLabels: false }
   * ```
   *
   * @example Hide ticks completely (axes only, no tick marks or labels)
   * ```ts
   * ticks: { visible: false }
   * ```
   */
  ticks?: TicksConfig;
}
